import { useTranslation } from 'react-i18next';
import { DeleteForeverOutlined, ExpandMore, ModeEditOutlineOutlined, WarningAmberRounded } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, alpha, IconButton, Tooltip, Typography } from '@mui/material';
import Constants from 'style/constants';
import PopUp from 'shared/components/pop-up/pop-up';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { SystemStatus } from 'features/systems/domain/models/system-status';
import { IS32AccordionType } from 'features/systems/IS32/domain/models/IS32-accordion-type';
import { IS32ConfigurationDeviceType } from 'features/systems/IS32/domain/models/IS32-configuration-device-type';
import { IS32ConfigurationUnit } from 'features/systems/IS32/domain/models/IS32-configuration-unit';
import { useIS32DetailsContextProvider } from 'features/systems/IS32/providers/IS32-details.provider';
import IS32ConfigurationAccordionDetailsItem from 'features/systems/IS32/views/IS32-configuration-accordion-details-item';
import styles from 'features/systems/IS32/views/IS32-configuration-accordion-item.module.scss';
import IS32EditConfigurationPopup from 'features/systems/IS32/views/IS32-edit-configuration.component';

interface Props {
  unit: IS32ConfigurationUnit;
}

const IS32ConfigurationAccordionItem = ({ unit }: Props) => {
  const { t } = useTranslation('IS32');
  const { hasPermission } = usePermissionAuthorization();

  const {
    systemsDetailsHook: { viewingMode, selectedSystem },
  } = useSystemsContextProvider();

  const {
    openConfigurationDetailsInEditingMode,
    deleteRoomUnit,
    isDeleteRoomUnitErrorPopupOpen,
    closeDeleteRoomUnitErrorPopup,
    getDeviceAlertsForRoomUnit,
    is32AccordionHook: { roomUnitExpanded, handleAccordionExpandedChange },
  } = useIS32DetailsContextProvider();

  const isSystemOffline = selectedSystem?.status === SystemStatus.offline;

  const shouldDisplayLocalBusUnits = unit.type === IS32ConfigurationDeviceType.Sec0106 || unit.type === IS32ConfigurationDeviceType.S0304;

  const accordionStyle = {
    border: '1px solid',
    borderColor: alpha(Constants.Colors.primary, 0.16),
    borderRadius: '16px',
    mb: 2,

    '&:first-of-type': {
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
    },

    '&:last-of-type': {
      borderBottomLeftRadius: '16px',
      borderBottomRightRadius: '16px',
    },
  };

  const accordionSummaryStyle = {
    minHeight: 0,
    padding: '8px 16px',

    '& .MuiAccordionSummary-content': {
      paddingTop: '8px',
      paddingBottom: '8px',
      margin: 0,
    },
    '&.Mui-expanded': {
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
      margin: 0,
      minHeight: 0,
      backgroundColor: Constants.Colors.secondaryContainer,

      '& .MuiAccordionSummary-content': {
        paddingTop: '8px',
        paddingBottom: '8px',
        margin: 0,
      },
    },
  };

  const formatUnitName = (unit: IS32ConfigurationUnit) => {
    const displayType = unit.displayType && unit.displayType.trim() !== '' ? unit.displayType : null;
    const type = unit.type && unit.type.trim() !== '' ? unit.type : '';
    const serial = unit.serial ? `-${unit.serial}` : '';
    const room = unit.room && unit.room.trim() !== '' ? `-${unit.room}` : '';
    return `${displayType ?? type}${serial}${room}`;
  };

  const showNewLabel = !unit.room || unit.room.trim() === '';

  const handleEditButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    openConfigurationDetailsInEditingMode();
  };

  const handleDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    deleteRoomUnit();
  };

  const monitoringIcon = () => {
    const deviceAlerts = getDeviceAlertsForRoomUnit(unit.address);

    if (deviceAlerts.length > 0) {
      return (
        <Tooltip
          title={
            <>
              {deviceAlerts.length === 1 ? (
                <div>
                  {t(`monitoring.${deviceAlerts[0].type}`, {
                    address: unit.address,
                    location: deviceAlerts[0].location,
                  })}
                </div>
              ) : (
                <ul className={styles.alertList}>
                  {deviceAlerts.map((alert) => (
                    <li key={`alert-${alert.type}-${alert.reference}`}>
                      {t(`monitoring.${alert.type}`, {
                        address: unit.address,
                        location: alert.location,
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </>
          }
        >
          <WarningAmberRounded className={styles.malfunctionIcon} />
        </Tooltip>
      );
    }
  };

  const formatLocalBusUnitTitle = () => {
    if (shouldDisplayLocalBusUnits) {
      if (unit.localBusUnits?.length === 1) {
        return t('configuration.unitTitleSingularLocalBusUnits', {
          address: unit.address,
          name: formatUnitName(unit),
          amountOfLocalBusUnits: unit.localBusUnits.length,
        });
      } else {
        return t('configuration.unitTitlePluralLocalBusUnits', {
          address: unit.address,
          name: formatUnitName(unit),
          amountOfLocalBusUnits: unit.localBusUnits?.length ?? 0,
        });
      }
    } else {
      return `${unit.address}: ${formatUnitName(unit)}`;
    }
  };

  return (
    <>
      <Accordion
        key={unit.address}
        sx={accordionStyle}
        expanded={roomUnitExpanded}
        onChange={(_, expanded) => handleAccordionExpandedChange(IS32AccordionType.RoomUnit, expanded)}
      >
        <AccordionSummary component="div" expandIcon={<ExpandMore />} sx={accordionSummaryStyle}>
          <div className={styles.headerContainer}>
            <Typography
              variant="h5"
              sx={{
                color: roomUnitExpanded ? Constants.Colors.onSecondaryContainer : '',
              }}
            >
              {formatLocalBusUnitTitle()}
            </Typography>
            {showNewLabel && (
              <Typography variant="overline" color="primary" className={styles.newLabel}>
                {t('configuration.newLabelRoom')}
              </Typography>
            )}
            {monitoringIcon()}
            {hasPermission(Permission.UpdateSystems) && (
              <div className={styles.buttonContainer}>
                <IconButton
                  onClick={(event) => handleDeleteButtonClick(event)}
                  disabled={viewingMode === 'editing' || isSystemOffline}
                  className={styles.deleteButton}
                >
                  <DeleteForeverOutlined />
                </IconButton>
                <IconButton
                  onClick={(event) => handleEditButtonClick(event)}
                  disabled={viewingMode === 'editing' || isSystemOffline}
                  className={styles.editButton}
                  sx={{
                    '&.Mui-disabled': {
                      backgroundColor: alpha(Constants.Colors.primary, 0.38),
                    },
                  }}
                >
                  <ModeEditOutlineOutlined className={viewingMode === 'editing' || isSystemOffline ? styles.disabledIcon : ''} />
                </IconButton>
              </div>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '8px 24px' }}>
          <IS32ConfigurationAccordionDetailsItem unit={unit} />
        </AccordionDetails>
      </Accordion>
      <IS32EditConfigurationPopup />

      <PopUp
        isOpen={isDeleteRoomUnitErrorPopupOpen}
        title={t('deleteRoomUnitError.title', {
          roomUnitName: unit.room ?? t('deleteRoomUnitError.roomUnitCapital'),
        })}
        body={t('deleteRoomUnitError.body', {
          roomUnitName: unit.room ?? t('deleteRoomUnitError.roomUnit'),
        })}
        primaryButtonText={t('deleteRoomUnitError.confirmButton')}
        handleOnClose={closeDeleteRoomUnitErrorPopup}
        primaryButtonAction={closeDeleteRoomUnitErrorPopup}
      />
    </>
  );
};

export default IS32ConfigurationAccordionItem;
